import { GroupingModeEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import moment from 'moment';
import { collectSelectableRowIds } from './time-report-table.utils';

/** Internal grouping bucket only — not a domain id. */
export const MISSING_GROUP_KEY = '__missing__';
/** Stable bucket for rows to which a WorkTime-only grouping field does not apply. */
export const NOT_APPLICABLE_GROUP_KEY = '__not_applicable__';
const NOT_APPLICABLE_TRANSLATION_KEY = 'time-report-grouping.not-applicable';

export interface TimeReportRowGroup {
  key: string;
  rows: TimeReportRowVM[];
  totalDuration: string;
  title: string | undefined;
  titleTranslationKey: string | undefined;
  date: Date | undefined;
  showDate: boolean;
}

/**
 * Whether the grouping field belongs to WorkTime rows.
 *
 * Non-WorkTime rows are still returned by {@link groupTimeReportRows} in the
 * not-applicable bucket.
 */
export function isWorkTimeOnlyGrouping(mode: GroupingModeEnum): boolean {
  return mode === GroupingModeEnum.PROJECT || mode === GroupingModeEnum.TASK;
}

export function groupTimeReportRows(rows: TimeReportRowVM[], mode: GroupingModeEnum): TimeReportRowGroup[] {
  if (mode === GroupingModeEnum.NONE) {
    return [];
  }

  const buckets = new Map<string, TimeReportRowVM[]>();
  for (const row of rows) {
    const key = resolveGroupKey(row, mode);
    if (key === undefined) {
      continue;
    }
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }

  const groups = [...buckets.entries()].map(([key, groupRows]) => toGroup(key, groupRows, mode));
  return groups.sort((left, right) => compareGroups(left, right, mode));
}

export function mergeGroupSelection(currentIds: string[], sessionIds: string[], allSelected: boolean): string[] {
  const validSessionIds = sessionIds.filter(id => id.length > 0);
  if (validSessionIds.length === 0) {
    return currentIds;
  }
  if (allSelected) {
    return currentIds.filter(id => !validSessionIds.includes(id));
  }
  return [...new Set([...currentIds, ...validSessionIds])];
}

export function selectableIdsForRows(rows: TimeReportRowVM[]): string[] {
  return collectSelectableRowIds(rows);
}

export function sumRowDurations(rows: TimeReportRowVM[]): string {
  const totalMinutes = rows.reduce((sum, row) => sum + moment.duration(row.duration).asMinutes(), 0);
  return moment.duration(totalMinutes, 'minutes').toISOString();
}

function resolveGroupKey(row: TimeReportRowVM, mode: GroupingModeEnum): string | undefined {
  if (isWorkTimeOnlyGrouping(mode) && row.kind !== 'WorkTime') {
    return NOT_APPLICABLE_GROUP_KEY;
  }
  switch (mode) {
    case GroupingModeEnum.USER:
      return row.userId || MISSING_GROUP_KEY;
    case GroupingModeEnum.DATE:
      return calendarDayKey(row.date);
    case GroupingModeEnum.USER_DATE:
      return `${row.userId || MISSING_GROUP_KEY}|${calendarDayKey(row.date)}`;
    case GroupingModeEnum.COMPANY:
      return row.companyId || MISSING_GROUP_KEY;
    case GroupingModeEnum.TYPE:
      return row.typeKey && row.typeTranslationKey ? row.typeKey : MISSING_GROUP_KEY;
    case GroupingModeEnum.PROJECT:
      return row.projectId || MISSING_GROUP_KEY;
    case GroupingModeEnum.TASK:
      return row.task?.id || MISSING_GROUP_KEY;
    default:
      return undefined;
  }
}

function calendarDayKey(date: Date | undefined): string {
  if (!date) {
    return MISSING_GROUP_KEY;
  }
  return moment(date).format('YYYY-MM-DD');
}

function toGroup(key: string, rows: TimeReportRowVM[], mode: GroupingModeEnum): TimeReportRowGroup {
  const first = rows[0];
  return {
    key,
    rows,
    totalDuration: sumRowDurations(rows),
    title: key === NOT_APPLICABLE_GROUP_KEY ? undefined : groupTitle(first, mode),
    titleTranslationKey:
      key === NOT_APPLICABLE_GROUP_KEY
        ? NOT_APPLICABLE_TRANSLATION_KEY
        : mode === GroupingModeEnum.TYPE && first?.typeTranslationKey
          ? first.typeTranslationKey
          : undefined,
    date: first?.date,
    showDate: mode === GroupingModeEnum.DATE || mode === GroupingModeEnum.USER_DATE
  };
}

function groupTitle(row: TimeReportRowVM | undefined, mode: GroupingModeEnum): string | undefined {
  switch (mode) {
    case GroupingModeEnum.USER:
    case GroupingModeEnum.USER_DATE:
      return nonempty(row?.userName);
    case GroupingModeEnum.COMPANY:
      return nonempty(row?.companyName);
    case GroupingModeEnum.PROJECT:
      return nonempty(row?.projectName);
    case GroupingModeEnum.TASK:
      return nonempty(row?.task?.name);
    default:
      return undefined;
  }
}

function nonempty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function compareGroups(left: TimeReportRowGroup, right: TimeReportRowGroup, mode: GroupingModeEnum): number {
  const rankDifference = groupRank(left.key, mode) - groupRank(right.key, mode);
  if (rankDifference !== 0) {
    return rankDifference;
  }
  if (mode === GroupingModeEnum.DATE || mode === GroupingModeEnum.USER_DATE) {
    return right.key.localeCompare(left.key);
  }
  const leftLabel = left.titleTranslationKey || left.title || left.key;
  const rightLabel = right.titleTranslationKey || right.title || right.key;
  return leftLabel.localeCompare(rightLabel);
}

function groupRank(key: string, mode: GroupingModeEnum): number {
  if (isMissingGroup(key, mode)) {
    return 2;
  }
  return key === NOT_APPLICABLE_GROUP_KEY ? 1 : 0;
}

function isMissingGroup(key: string, mode: GroupingModeEnum): boolean {
  if (mode === GroupingModeEnum.USER_DATE) {
    return key.includes(MISSING_GROUP_KEY);
  }
  return key === MISSING_GROUP_KEY;
}
